import React, { useMemo } from 'react'
import * as XLSX from 'xlsx'
import jsPDF from 'jspdf'
import { history } from '../store'

// ─── Battery thresholds ──────────────────────────────────────────────────────
// These mirror the "battery view" grid (MatrixConstructorBattery):
//   configured & Bateria < 4400        → red cross (#FL)  → "Sin batería"
//   configured & 4400 <= Bateria < 5800 → orange (#RK)     → "Poca batería"
//   configured & Bateria >= 5800        → green  (#AV)     → OK
//   not configured                      → black  (#Null)   → "Color Negro"
const MIN_BATTERY = 4400
const MAX_BATTERY = 6500
const LOW_BATTERY = 5800

// Fault code → Spanish label (same wording as the server PDF report)
const FAULT_LABELS = {
  BROKEN_LOCK: 'Pincho roto',
  GREEN_BLOCK: 'Pin verde no abre',
  RED_BLOCK: 'Pin rojo no abre',
  PINPAD_BLOCK: 'Teclado no enciende',
  PINPAD_ACTIVE: 'Teclado encendido',
  DOOR_BLOCK: 'Puerta no cierra',
}

function batteryPercent(bateria) {
  const pct = ((bateria - MIN_BATTERY) / (MAX_BATTERY - MIN_BATTERY)) * 100
  return Math.max(0, Math.min(100, Math.round(pct)))
}

/**
 * Build the fault analysis rows for a single customer.
 * Each row = one locker with a fault, black (unconfigured) door, or battery issue.
 */
export function buildFaultAnalysis(matrixs, machines, customerName) {
  const rows = []

  ;(matrixs || []).forEach((matrix) => {
    const machine = (machines || []).find((m) => m.Serial === matrix.Serial)
    if (!machine) return

    const name =
      machine.Customer && machine.Customer.Fullname ? machine.Customer.Fullname : ''
    if (name !== customerName) return

    const firstIndex = Number(machine.FirstIndex) || 0
    const lastIndex =
      Number(machine.LastIndex) || (matrix.Locker ? matrix.Locker.length : 0)

    ;(matrix.Locker || []).forEach((lock, index) => {
      // Only lockers within the machine's active range are drawn in the grid
      if (index >= lastIndex) return

      const taquilla = index + firstIndex
      const configured = !!lock.Configured
      const hasFault = lock.FaultCode !== undefined && lock.Status === 'FL'
      const bateria = Number(lock.Bateria)
      const hasReading = Number.isFinite(bateria)

      // Battery classification only applies to configured lockers (as in the grid)
      let batteryState = null // 'SIN' | 'POCA' | 'OK'
      if (configured && hasReading) {
        if (bateria < MIN_BATTERY) batteryState = 'SIN'
        else if (bateria < LOW_BATTERY) batteryState = 'POCA'
        else batteryState = 'OK'
      }
      const pct = configured && hasReading ? batteryPercent(bateria) : null
      const batteryLabel =
        batteryState === 'SIN'
          ? 'Sin batería'
          : batteryState === 'POCA'
          ? 'Poca batería'
          : ''

      // Not configured → black door
      if (!configured) {
        rows.push({
          serial: machine.Serial,
          taquilla,
          kind: 'NEGRO',
          averia: 'Color Negro',
          batteryLabel: '',
          pct: null,
        })
        return
      }

      if (hasFault) {
        // Fault (optionally with a co-occurring battery issue + %)
        rows.push({
          serial: machine.Serial,
          taquilla,
          kind: 'AVERIA',
          averia: FAULT_LABELS[lock.FaultCode] || lock.FaultCode,
          batteryLabel,
          pct,
        })
      } else if (batteryState === 'SIN') {
        // No fault, but no battery → its own entry
        rows.push({
          serial: machine.Serial,
          taquilla,
          kind: 'SIN',
          averia: 'Sin batería',
          batteryLabel: '',
          pct,
        })
      } else if (batteryState === 'POCA') {
        // No fault, but low battery → its own entry
        rows.push({
          serial: machine.Serial,
          taquilla,
          kind: 'POCA',
          averia: 'Poca batería',
          batteryLabel: '',
          pct,
        })
      }
      // else: configured + healthy + no fault → nothing to report
    })
  })

  // Every row belongs to the requested customer — stamp it so the ordering is
  // explicit and stays correct if the data is ever aggregated across customers.
  rows.forEach((r) => { r.customer = customerName || '' })

  // Always sort by customer → machine serial → locker number (in that order).
  rows.sort(
    (a, b) =>
      (a.customer || '').localeCompare(b.customer || '', 'es', { sensitivity: 'base' }) ||
      (a.serial || '').localeCompare(b.serial || '', undefined, { numeric: true, sensitivity: 'base' }) ||
      (a.taquilla - b.taquilla)
  )
  return rows
}

/** Render a row as the single-line format the report uses. */
export function faultRowToLine(r) {
  let line = `Máquina: ${r.serial}  |  Taquilla: ${r.taquilla}  |  Avería: ${r.averia}`
  if (r.batteryLabel) line += `  |  ${r.batteryLabel}`
  if (r.pct != null) line += `  |  ${r.pct}% de batería`
  return line
}

function safeCustomer(customer) {
  return (customer || 'cliente').replace(/[^a-z0-9]+/gi, '_')
}

/** Download the customer's fault analysis as an Excel file. */
export function downloadFaultAnalysisExcel(matrixs, machines, customer) {
  const rows = buildFaultAnalysis(matrixs, machines, customer)
  const data = rows.map((r) => ({
    Máquina: r.serial,
    Taquilla: r.taquilla,
    Avería: r.averia,
    Batería: r.batteryLabel,
    '% Batería': r.pct != null ? `${r.pct}%` : '',
  }))
  const ws = XLSX.utils.json_to_sheet(data, {
    header: ['Máquina', 'Taquilla', 'Avería', 'Batería', '% Batería'],
  })
  ws['!cols'] = [{ wch: 14 }, { wch: 10 }, { wch: 22 }, { wch: 16 }, { wch: 12 }]
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, 'Análisis')
  XLSX.writeFile(
    wb,
    `analisis-averias-${safeCustomer(customer)}-${new Date()
      .toISOString()
      .slice(0, 10)}.xlsx`
  )
}

/** Download the customer's fault analysis as a PDF file (same content as Excel). */
export function downloadFaultAnalysisPDF(matrixs, machines, customer) {
  const rows = buildFaultAnalysis(matrixs, machines, customer)
  const doc = new jsPDF({ unit: 'pt', format: 'a4' })
  const pageW = doc.internal.pageSize.getWidth()
  const pageH = doc.internal.pageSize.getHeight()
  const marginX = 48
  let y = 60

  doc.setFont('helvetica', 'bold')
  doc.setFontSize(22)
  doc.text('Informe de Averías', pageW / 2, y, { align: 'center' })
  y += 22
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(13)
  doc.text('VACWAYlockers', pageW / 2, y, { align: 'center' })
  y += 32

  const now = new Date()
  const fecha = `${now.toLocaleDateString('es-ES')} ${now.toLocaleTimeString(
    'es-ES',
    { hour: '2-digit', minute: '2-digit', hour12: false }
  )}`
  doc.setFontSize(11)
  doc.text(`Cliente: ${customer || '-'}`, marginX, y)
  y += 16
  doc.text(`Fecha: ${fecha}`, marginX, y)
  y += 22

  const count = (k) => rows.filter((r) => r.kind === k).length
  doc.setFont('helvetica', 'bold')
  doc.text(`Total incidencias: ${rows.length}`, marginX, y)
  y += 15
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(10)
  doc.text(
    `Averías: ${count('AVERIA')}    ·    Color Negro: ${count(
      'NEGRO'
    )}    ·    Poca batería: ${count('POCA')}    ·    Sin batería: ${count(
      'SIN'
    )}`,
    marginX,
    y
  )
  y += 26

  doc.setFont('helvetica', 'bold')
  doc.setFontSize(14)
  doc.text('Listado de averías', marginX, y)
  y += 20

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(10)
  const lineH = 16
  if (rows.length === 0) {
    doc.text('Sin incidencias.', marginX, y)
  } else {
    rows.forEach((r) => {
      if (y > pageH - 48) {
        doc.addPage()
        y = 60
      }
      doc.text(faultRowToLine(r), marginX, y)
      y += lineH
    })
  }

  doc.save(
    `analisis-averias-${safeCustomer(customer)}-${now
      .toISOString()
      .slice(0, 10)}.pdf`
  )
}

// ─── Modal ───────────────────────────────────────────────────────────────────

const FaultsAnalysisModal = ({ open, onClose, matrixs, machines, customer }) => {
  const rows = useMemo(
    () => (open ? buildFaultAnalysis(matrixs, machines, customer) : []),
    [open, matrixs, machines, customer]
  )

  if (!open) return null

  const groups = {
    AVERIA: rows.filter((r) => r.kind === 'AVERIA'),
    NEGRO: rows.filter((r) => r.kind === 'NEGRO'),
    POCA: rows.filter((r) => r.kind === 'POCA'),
    SIN: rows.filter((r) => r.kind === 'SIN'),
  }

  const renderItem = (r, i) => (
    <button
      key={`${r.serial}-${r.taquilla}-${i}`}
      type="button"
      className="ns-analysis-item"
      onClick={() => history.push(`/machines/${r.serial}`)}
      title="Abrir máquina"
    >
      <div className="ns-analysis-item__main">
        <div className="ns-analysis-item__serial">
          {r.serial} · Taquilla {r.taquilla}
        </div>
        <div className="ns-analysis-item__customer">
          {r.averia}
          {r.batteryLabel ? ` · ${r.batteryLabel}` : ''}
        </div>
      </div>
      <div className="ns-analysis-item__meta">
        {r.pct != null && (
          <span
            style={{
              display: 'inline-block',
              borderRadius: 999,
              padding: '2px 8px',
              fontWeight: 700,
              fontSize: 11.5,
              background: r.pct < 33 ? '#fee2e2' : r.pct < 67 ? '#ffedd5' : '#dcfce7',
              color: r.pct < 33 ? '#b91c1c' : r.pct < 67 ? '#c2410c' : '#166534',
            }}
          >
            {r.pct}% batería
          </span>
        )}
      </div>
    </button>
  )

  const renderSection = (title, list, headBg, headColor, countBg) => (
    <div className="ns-analysis-section">
      <div
        className="ns-analysis-section__head"
        style={{ background: headBg, color: headColor }}
      >
        <span>{title}</span>
        <span
          className="ns-analysis-count"
          style={{ background: countBg, color: '#fff' }}
        >
          {list.length}
        </span>
      </div>
      <div className="ns-analysis-list">
        {list.length === 0 ? (
          <div className="ns-analysis-empty">Ninguna ✓</div>
        ) : (
          list.map(renderItem)
        )}
      </div>
    </div>
  )

  const total = rows.length

  return (
    <div className="ns-analysis-overlay" onClick={onClose}>
      <div className="ns-analysis-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ns-analysis-header">
          <div>
            <h2>Análisis de averías — {customer || 'Cliente'}</h2>
            <div className="ns-analysis-sub">
              Averías, taquillas en negro y estado de batería · {total}{' '}
              {total === 1 ? 'incidencia' : 'incidencias'}
            </div>
          </div>
          <div className="ns-analysis-actions">
            <button
              className="nightscan-csv-btn"
              onClick={() => downloadFaultAnalysisExcel(matrixs, machines, customer)}
              disabled={total === 0}
              title="Descargar análisis en Excel"
            >
              <i className="fas fa-file-excel"></i>
              Excel
            </button>
            <button
              className="nightscan-csv-btn"
              onClick={() => downloadFaultAnalysisPDF(matrixs, machines, customer)}
              disabled={total === 0}
              title="Descargar análisis en PDF"
              style={{ background: 'linear-gradient(135deg, #b91c1c, #ef4444)' }}
            >
              <i className="fas fa-file-pdf"></i>
              PDF
            </button>
            <button
              className="ns-analysis-close"
              onClick={onClose}
              aria-label="Cerrar"
            >
              ✕
            </button>
          </div>
        </div>
        <div className="ns-analysis-body">
          {total === 0 ? (
            <div className="ns-analysis-empty" style={{ padding: 32 }}>
              Este cliente no tiene averías, taquillas en negro ni incidencias de
              batería ✓
            </div>
          ) : (
            <>
              <div className="ns-analysis-grid">
                {renderSection(
                  'Averías',
                  groups.AVERIA,
                  '#fef2f2',
                  '#b91c1c',
                  '#b91c1c'
                )}
                {renderSection(
                  'Taquillas en negro',
                  groups.NEGRO,
                  '#f1f5f9',
                  '#0f172a',
                  '#0f172a'
                )}
              </div>
              <div className="ns-analysis-grid">
                {renderSection(
                  'Poca batería',
                  groups.POCA,
                  '#fff7ed',
                  '#c2410c',
                  '#c2410c'
                )}
                {renderSection(
                  'Sin batería',
                  groups.SIN,
                  '#fef2f2',
                  '#b91c1c',
                  '#b91c1c'
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default FaultsAnalysisModal
